import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, AppBar, Toolbar, Typography, Button, Card, CardContent,
  IconButton, CircularProgress, MenuItem, TextField, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress,
  Tooltip
} from '@mui/material';
import {
  ArrowBack, Map, CheckCircle, Warning, Schedule, Close,
  Assignment, FilterList
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { API_URL } from './config';

interface Area { id: number; nome: string; zona: string; }
interface Ciclo { id: number; nome: string; ativo: boolean; }
interface Quarteirao {
  id: number; numero: number; descricao: string | null;
  area_id: number; status: string; atualizado_em: string | null;
}
interface Fad01Mini {
  id: number; data: string; atividade: string;
  agente: { nome: string; matricula: string };
  imoveis_residencia: number; imoveis_comercio: number;
  imoveis_terreno_baldio: number; imoveis_ponto_estrategico: number; imoveis_outros: number;
  inspecionados: number; imoveis_positivos: number;
  quarteirao_concluido: boolean;
}

interface Props { onVoltar: () => void; }

const STATUS_CONFIG = {
  concluido:    { cor: '#065F46', bg: '#D1FAE5', borda: '#6EE7B7', icone: <CheckCircle sx={{ fontSize: 18 }} />, label: 'Concluído' },
  em_andamento: { cor: '#92400E', bg: '#FEF3C7', borda: '#FCD34D', icone: <Schedule sx={{ fontSize: 18 }} />,   label: 'Em andamento' },
  pendente:     { cor: '#991B1B', bg: '#FEF2F2', borda: '#FCA5A5', icone: <Warning sx={{ fontSize: 18 }} />,    label: 'Pendente' },
};

export default function Cobertura({ onVoltar }: Props) {
  const { token } = useAuth();
  const [areas, setAreas] = useState<Area[]>([]);
  const [ciclos, setCiclos] = useState<Ciclo[]>([]);
  const [quarteiraos, setQuarteiraos] = useState<Quarteirao[]>([]);
  const [areaId, setAreaId] = useState<string>('');
  const [cicloId, setCicloId] = useState<string>('');
  const [carregando, setCarregando] = useState(true);
  const [carregandoQt, setCarregandoQt] = useState(false);
  const [historicoQt, setHistoricoQt] = useState<Fad01Mini[]>([]);
  const [qtSelecionado, setQtSelecionado] = useState<Quarteirao | null>(null);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  const h = { Authorization: `Bearer ${token}` };

  const carregarBase = useCallback(async () => {
    try {
      const [ra, rc] = await Promise.all([
        axios.get(`${API_URL}/api/areas/`, { headers: h }),
        axios.get(`${API_URL}/api/ciclos/`, { headers: h }),
      ]);
      const areasAtivas = ra.data.filter((a: Area & { ativa: boolean }) => a.ativa);
      setAreas(areasAtivas);
      setCiclos(rc.data);
      const cicloAtivo = rc.data.find((c: Ciclo) => c.ativo);
      if (cicloAtivo) setCicloId(String(cicloAtivo.id));
      if (areasAtivas.length > 0) setAreaId(String(areasAtivas[0].id));
    } catch {
      // silent
    } finally {
      setCarregando(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const carregarQuarteiraos = useCallback(async (aid: string) => {
    if (!aid) { setQuarteiraos([]); return; }
    setCarregandoQt(true);
    try {
      const r = await axios.get(`${API_URL}/api/quarteirao/area/${aid}`, { headers: h });
      setQuarteiraos(r.data);
    } catch {
      setQuarteiraos([]);
    } finally {
      setCarregandoQt(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { carregarBase(); }, [carregarBase]);
  useEffect(() => { carregarQuarteiraos(areaId); }, [areaId, carregarQuarteiraos]);

  const abrirHistorico = async (qt: Quarteirao) => {
    setQtSelecionado(qt);
    setCarregandoHistorico(true);
    try {
      const params: any = { quarteirao_id: qt.id };
      if (cicloId) params.ciclo_id = cicloId;
      const r = await axios.get(`${API_URL}/api/fad01/`, { headers: h, params });
      setHistoricoQt(r.data);
    } catch {
      setHistoricoQt([]);
    } finally {
      setCarregandoHistorico(false);
    }
  };

  // Métricas
  const total = quarteiraos.length;
  const concluidos = quarteiraos.filter(q => q.status === 'concluido').length;
  const emAndamento = quarteiraos.filter(q => q.status === 'em_andamento').length;
  const pendentes = quarteiraos.filter(q => q.status === 'pendente').length;
  const pct = total > 0 ? Math.round(concluidos / total * 100) : 0;
  const corPct = pct >= 80 ? '#065F46' : pct >= 40 ? '#92400E' : '#991B1B';

  if (carregando) return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1B3A6B, #2D5A9E)' }}>
      <CircularProgress sx={{ color: 'white' }} size={50} />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F0F4FF', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static" sx={{ background: 'linear-gradient(135deg, #0F2347, #1B3A6B, #2D5A9E)' }}>
        <Toolbar>
          <IconButton color="inherit" onClick={onVoltar} sx={{ mr: 1 }}><ArrowBack /></IconButton>
          <Map sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>Cobertura de Área</Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3, maxWidth: 1100, margin: '0 auto', width: '100%', flexGrow: 1 }}>

        {/* ── Filtros ── */}
        <Card sx={{ borderRadius: 3, mb: 3, border: '1px solid #E5E7EB' }}>
          <CardContent sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FilterList sx={{ color: '#9CA3AF' }} />
            <TextField
              select label="Área" value={areaId} size="small" sx={{ minWidth: 200 }}
              onChange={e => setAreaId(e.target.value)}
            >
              {areas.length === 0
                ? <MenuItem value="" disabled>Nenhuma área cadastrada</MenuItem>
                : areas.map(a => <MenuItem key={a.id} value={String(a.id)}>{a.nome} ({a.zona})</MenuItem>)
              }
            </TextField>
            <TextField
              select label="Ciclo (filtro do histórico)" value={cicloId} size="small" sx={{ minWidth: 220 }}
              onChange={e => setCicloId(e.target.value)}
            >
              <MenuItem value="">Todos os ciclos</MenuItem>
              {ciclos.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.nome}{c.ativo ? ' (ativo)' : ''}</MenuItem>)}
            </TextField>
          </CardContent>
        </Card>

        {areas.length === 0 ? (
          <Card sx={{ borderRadius: 3, textAlign: 'center', p: 8, border: '2px dashed #E5E7EB' }}>
            <Map sx={{ fontSize: 64, color: '#D1D5DB', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#6B7280' }}>Nenhuma área cadastrada</Typography>
            <Typography variant="body2" sx={{ color: '#9CA3AF', mt: 0.5 }}>Cadastre áreas e quarteirões em Configurações.</Typography>
          </Card>
        ) : (
          <>
            {/* ── Painel de métricas ── */}
            {total > 0 && (
              <Card sx={{ borderRadius: 3, mb: 3, background: 'linear-gradient(135deg, #0F2347, #1B3A6B)', color: 'white' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="h2" sx={{ fontWeight: 900, lineHeight: 1, color: pct >= 80 ? '#6EE7B7' : pct >= 40 ? '#FCD34D' : '#FCA5A5' }}>
                        {pct}%
                      </Typography>
                      <Typography sx={{ opacity: 0.7, fontSize: '0.85rem' }}>de cobertura</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ textAlign: 'center', px: 2, py: 1, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                        <Typography variant="h4" sx={{ fontWeight: 900, color: '#6EE7B7' }}>{concluidos}</Typography>
                        <Typography sx={{ opacity: 0.7, fontSize: '0.75rem' }}>Concluídos</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', px: 2, py: 1, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                        <Typography variant="h4" sx={{ fontWeight: 900, color: '#FCD34D' }}>{emAndamento}</Typography>
                        <Typography sx={{ opacity: 0.7, fontSize: '0.75rem' }}>Em andamento</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', px: 2, py: 1, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                        <Typography variant="h4" sx={{ fontWeight: 900, color: '#FCA5A5' }}>{pendentes}</Typography>
                        <Typography sx={{ opacity: 0.7, fontSize: '0.75rem' }}>Pendentes</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', px: 2, py: 1, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                        <Typography variant="h4" sx={{ fontWeight: 900 }}>{total}</Typography>
                        <Typography sx={{ opacity: 0.7, fontSize: '0.75rem' }}>Total</Typography>
                      </Box>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate" value={pct}
                    sx={{
                      height: 10, borderRadius: 5,
                      bgcolor: 'rgba(255,255,255,0.15)',
                      '& .MuiLinearProgress-bar': { bgcolor: corPct, borderRadius: 5 },
                    }}
                  />
                </CardContent>
              </Card>
            )}

            {/* ── Grade de quarteirões ── */}
            {carregandoQt ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : quarteiraos.length === 0 ? (
              <Card sx={{ borderRadius: 3, textAlign: 'center', p: 8, border: '2px dashed #E5E7EB' }}>
                <Map sx={{ fontSize: 64, color: '#D1D5DB', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#6B7280' }}>Nenhum quarteirão cadastrado nesta área</Typography>
                <Typography variant="body2" sx={{ color: '#9CA3AF', mt: 0.5 }}>
                  Cadastre os quarteirões em Configurações → Quarteirões.
                </Typography>
              </Card>
            ) : (
              <>
                <Typography variant="body2" sx={{ color: '#6B7280', mb: 2, fontWeight: 600 }}>
                  Clique em um quarteirão para ver o histórico de visitas
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {quarteiraos.map(q => {
                    const cfg = STATUS_CONFIG[q.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.pendente;
                    return (
                      <Tooltip
                        key={q.id}
                        title={`Quarteirão ${q.numero}${q.descricao ? ` · ${q.descricao}` : ''} — ${cfg.label}${q.atualizado_em ? ` (${new Date(q.atualizado_em).toLocaleDateString('pt-BR')})` : ''}`}
                        arrow
                      >
                        <Card
                          onClick={() => abrirHistorico(q)}
                          sx={{
                            width: 88, height: 88,
                            borderRadius: 3,
                            border: `2px solid ${cfg.borda}`,
                            bgcolor: cfg.bg,
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.18s',
                            '&:hover': { transform: 'scale(1.08)', boxShadow: `0 6px 20px ${cfg.borda}80` },
                          }}
                        >
                          <Box sx={{ color: cfg.cor, mb: 0.3 }}>{cfg.icone}</Box>
                          <Typography sx={{ fontWeight: 900, fontSize: '1.5rem', color: cfg.cor, lineHeight: 1 }}>
                            {q.numero}
                          </Typography>
                          {q.descricao && (
                            <Typography sx={{ fontSize: '0.58rem', color: cfg.cor, opacity: 0.75, mt: 0.3, px: 0.5, textAlign: 'center', lineHeight: 1.2, maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {q.descricao}
                            </Typography>
                          )}
                        </Card>
                      </Tooltip>
                    );
                  })}
                </Box>

                {/* Legenda */}
                <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      <Box sx={{ width: 16, height: 16, borderRadius: 1, bgcolor: cfg.bg, border: `2px solid ${cfg.borda}` }} />
                      <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600 }}>{cfg.label}</Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </>
        )}
      </Box>

      <Box sx={{ py: 2, textAlign: 'center', borderTop: '1px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
        <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
          © 2026 Tovis · <strong style={{ color: '#6B7280' }}>Walber Reis</strong>
        </Typography>
      </Box>

      {/* ── Dialog Histórico do Quarteirão ── */}
      <Dialog
        open={qtSelecionado !== null}
        onClose={() => setQtSelecionado(null)}
        maxWidth="sm" fullWidth
        slotProps={{ paper: { sx: { borderRadius: 3 } } }}
      >
        {qtSelecionado && (() => {
          const cfg = STATUS_CONFIG[qtSelecionado.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.pendente;
          return (
            <>
              <DialogTitle sx={{ fontWeight: 700, color: '#1F2937', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: cfg.bg, border: `2px solid ${cfg.borda}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', color: cfg.cor }}>{qtSelecionado.numero}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>Quarteirão {qtSelecionado.numero}</Typography>
                      {qtSelecionado.descricao && (
                        <Typography variant="caption" sx={{ color: '#6B7280' }}>{qtSelecionado.descricao}</Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label={cfg.label} size="small" sx={{ bgcolor: cfg.bg, color: cfg.cor, fontWeight: 700 }} />
                    <IconButton size="small" onClick={() => setQtSelecionado(null)} sx={{ color: '#9CA3AF' }}><Close /></IconButton>
                  </Box>
                </Box>
              </DialogTitle>

              <DialogContent sx={{ pt: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#6B7280', fontWeight: 700, mb: 1.5, textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.08em' }}>
                  Histórico de visitas{cicloId ? ' (ciclo selecionado)' : ''}
                </Typography>

                {carregandoHistorico ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={32} /></Box>
                ) : historicoQt.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Assignment sx={{ fontSize: 48, color: '#D1D5DB', mb: 1 }} />
                    <Typography sx={{ color: '#9CA3AF' }}>Nenhuma visita registrada</Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {historicoQt.map(r => {
                      const totIm = r.imoveis_residencia + r.imoveis_comercio + r.imoveis_terreno_baldio + r.imoveis_ponto_estrategico + r.imoveis_outros;
                      return (
                        <Card key={r.id} sx={{ borderRadius: 2, border: `1px solid ${r.quarteirao_concluido ? '#A7F3D0' : '#E5E7EB'}`, bgcolor: r.quarteirao_concluido ? '#F0FDF4' : 'white' }}>
                          <CardContent sx={{ p: 2, pb: '8px !important' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box>
                                <Typography sx={{ fontWeight: 700, color: '#1F2937', fontSize: '0.9rem' }}>
                                  {new Date(r.data + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short' })}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#6B7280' }}>{r.agente.nome}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                <Chip label={r.atividade} size="small" sx={{ bgcolor: '#EFF6FF', color: '#1B3A6B', fontWeight: 700, fontSize: '0.65rem' }} />
                                {r.quarteirao_concluido && (
                                  <Chip label="Concluído" size="small" sx={{ bgcolor: '#D1FAE5', color: '#065F46', fontWeight: 700, fontSize: '0.65rem' }} />
                                )}
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                              <Typography variant="caption" sx={{ color: '#6B7280' }}>{totIm} imóveis</Typography>
                              <Typography variant="caption" sx={{ color: '#6B7280' }}>·</Typography>
                              <Typography variant="caption" sx={{ color: '#6B7280' }}>{r.inspecionados} inspecionados</Typography>
                              {r.imoveis_positivos > 0 && (
                                <>
                                  <Typography variant="caption" sx={{ color: '#6B7280' }}>·</Typography>
                                  <Typography variant="caption" sx={{ color: '#991B1B', fontWeight: 700 }}>{r.imoveis_positivos} focos</Typography>
                                </>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                )}
              </DialogContent>

              <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setQtSelecionado(null)} sx={{ color: '#6B7280' }}>Fechar</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Box>
  );
}
